/* eslint-disable complexity */
import * as Sentry from '@sentry/vue';
import pkg from '@/../../package.json';
import { initialState } from '@/Helpers.js';
import axios from '@/plugins/axios.js';
import { recoverChunkError } from '@/router/chunkRecoveryBridge.js';
import isAccessDeniedError from '@/router/interceptors/isAccessDeniedError.js';
import { isChunkLoadError } from '@/router/isChunkLoadError.js';
import store from '@/store/Index.js';
import ErrorPackage from '@/utilities/ErrorPackage.js';

// Define error patterns to ignore
const ignoreErrors = [
    // Froala Editor errors
    "Cannot read property 'html' of null",
    "Cannot read properties of null (reading 'html')",
    "Cannot read property '1' of undefined",
    "Cannot read property 'nodeType' of null",
    "NoModificationAllowedError: Failed to set the 'outerHTML' property on 'Element': This element has no parent node.",
    'NoModificationAllowedError: The object can not be modified.',
    'n[r] is undefined',
    'r("#".concat(r(this).attr("for"))).prop is not a function',

    // ResizeObserver errors (known browser issue)
    'ResizeObserver loop limit exceeded',
    'Error: ResizeObserver loop limit exceeded',

    // Web Spell Checker errors
    "null is not an object (evaluating 't.nextSibling.innerText')",
    "Cannot read property 'register' of null",
    'null is not an object (evaluating \'b.getService("Deferred").register\')',
    "Cannot read property 'operation' of undefined",
    'b.getService(...) is null',
    'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',

    // Buefy errors
    'e.getHours is not a function',

    // Vue Router navigation errors
    /^Navigation cancelled from .+ to .+$/,
    /^Navigation aborted from .+ to .+$/,

    // Chrome API errors
    "Can't find variable: chrome",

    // Browser extension errors (non-actionable)
    /^Error: null$/,
    /^null$/,

    // Undefined promise rejections (cannot be debugged, Safari-specific)
    'Error: undefined',
    /^undefined$/,

    // Axios cancel token errors (intentional request cancellation)
    // Use regex to match all AbortError variations
    /\bAbortError\b/,
    /^Cancel.*canceled$/i,
    /^Request.*canceled$/i,

    // Network errors (user network disconnections, browser throttling, etc.)
    /^AxiosError: Network Error$/,
    'Network Error',
];

// Format errors for better matching
const formattedErrors = ignoreErrors.filter(error => !(error instanceof RegExp)).map(error => `TypeError: ${error}`);

ignoreErrors.push(...formattedErrors);

// Define public routes that don't require authentication
const publicPaths = ['/', '/login', '/password/email', '/password/reset'];

// Helper to check if a path is a public route
function isPublicRoutePath(currentPath) {
    return publicPaths.some(path => currentPath === path || (path !== '/' && currentPath.startsWith(`${path}/`)));
}

// Browser-safe context gathering functions
const safeGetBrowserInfo = () => {
    const info = {};

    try {
        info.appUrl = import.meta.env.VITE_APP_URL || 'unknown';
    } catch {
        info.appUrl = 'unknown';
    }

    try {
        info.environment = import.meta.env.MODE || 'unknown';
    } catch {
        info.environment = 'unknown';
    }

    try {
        info.timestamp = new Date().toISOString();
    } catch {
        info.timestamp = 'unknown';
    }

    try {
        info.userAgent = navigator.userAgent || 'unknown';
    } catch {
        info.userAgent = 'unknown';
    }

    try {
        info.screenResolution = `${window.screen.width || 0}x${window.screen.height || 0}`;
    } catch {
        info.screenResolution = 'unknown';
    }

    try {
        info.viewport = `${window.innerWidth || 0}x${window.innerHeight || 0}`;
    } catch {
        info.viewport = 'unknown';
    }

    try {
        info.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown';
    } catch {
        info.timezone = 'unknown';
    }

    try {
        info.locale = navigator.language || 'unknown';
    } catch {
        info.locale = 'unknown';
    }

    try {
        info.onlineStatus = navigator.onLine;
    } catch {
        info.onlineStatus = true; // Assume online if can't determine
    }

    return info;
};

const safeGetUserContext = () => {
    try {
        if (store?.state?.user) {
            return {
                id: store.state.user.id || 'unknown',
                email: store.state.user.email || 'unknown',
                role: store.state.user.role || 'unknown',
            };
        }
    } catch (_error) {
        // Silently fail - user context is not critical
    }
    return null;
};

const safeGetRouteContext = router => {
    try {
        if (router?.currentRoute?.value) {
            const route = router.currentRoute.value;
            return {
                tags: {
                    route: route.name || route.path || 'unknown',
                    route_path: route.path || 'unknown',
                },
                context: {
                    name: route.name || 'unknown',
                    path: route.path || 'unknown',
                    params: route.params || {},
                    query: route.query || {},
                },
            };
        }
    } catch (_error) {
        // Silently fail - route context is not critical
    }
    return { tags: {}, context: {} };
};

// Centralized function to detect cancellation errors
// Supports both CancelToken (axios.isCancel) and AbortController (ERR_CANCELED/CanceledError/AbortError)
function isCancellationError(error) {
    if (!error) {
        return false;
    }

    // Promise rejections can be strings
    if (typeof error === 'string') {
        return /\bAbortError\b/i.test(error) || /\bERR_CANCELED\b/i.test(error) || /\bcancell?ed\b/i.test(error);
    }

    // Check for axios canceled requests (CancelToken)
    if (axios?.isCancel?.(error)) {
        return true;
    }

    // Check for AbortController cancellation errors
    if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError') {
        return true;
    }

    // Check error message for cancellation patterns using word boundaries to avoid false positives
    const message = typeof error.message === 'string' ? error.message : '';
    if (message) {
        if (/\bAbortError\b/i.test(message)) return true;
        if (/\bERR_CANCELED\b/i.test(message)) return true;
        if (/\bcancell?ed\b/i.test(message)) return true;
    }

    return false;
}

// Top-level Replay sample rates (must not be passed to the Replay constructor —
// newer @sentry/vue SDKs warn and ignore sessionSampleRate/errorSampleRate there).
export const REPLAYS_SESSION_SAMPLE_RATE = 0.0;
export const REPLAYS_ON_ERROR_SAMPLE_RATE = 1.0;

export function getReplaySampleRates() {
    return {
        replaysSessionSampleRate: REPLAYS_SESSION_SAMPLE_RATE,
        replaysOnErrorSampleRate: REPLAYS_ON_ERROR_SAMPLE_RATE,
    };
}

export default {
    install(app, { router }) {
        Sentry.init({
            dsn: initialState('SentryDsn'),
            environment: initialState('Env'),
            release: pkg.version,
            logErrors: initialState('Debug'),
            allowUrls: [/js/],
            ignoreErrors,
            // Capture 0% of sessions by default; capture 100% of sessions with errors
            ...getReplaySampleRates(),
            // Enhanced sampling rate aligned with backend
            tracesSampleRate: initialState('Env') === 'production' ? 0.2 : 1.0,

            // Intelligent sampling for educational workflows
            tracesSampler: samplingContext => {
                if (initialState('Env') !== 'production') {
                    return 1.0; // 100% in development
                }

                const routeName = samplingContext.location?.pathname || '';

                // High priority educational workflows - match backend sampling
                if (
                    routeName.includes('submissions') ||
                    routeName.includes('gradebook') ||
                    routeName.includes('grading') ||
                    routeName.includes('submit-activity')
                ) {
                    return 0.5; // 50% for critical workflows
                }

                // Medium priority workflows
                if (
                    routeName.includes('quiz') ||
                    routeName.includes('assessment') ||
                    routeName.includes('courses') ||
                    routeName.includes('upload')
                ) {
                    return 0.3; // 30% for medium priority
                }

                // Low priority workflows
                if (
                    routeName.includes('dashboard') ||
                    routeName.includes('reports') ||
                    routeName.includes('analytics')
                ) {
                    return 0.1; // 10% for analytics
                }

                return 0.2; // 20% default
            },

            attachStacktrace: true,
            app,

            // CONSULTATION ENHANCEMENT: Vue component tracking configuration
            trackComponents: true,
            attachProps: true,
            hooks: ['activate', 'create', 'mount', 'update', 'unmount'],
            integrations: [
                new Sentry.BrowserTracing({
                    routingInstrumentation: Sentry.vueRouterInstrumentation(router),
                    // Enhanced trace propagation for distributed tracing
                    tracePropagationTargets: [
                        'localhost',
                        'edverum.test',
                        /^\//, // Same-origin app requests.
                        // Keep distributed tracing on absolute Edverum app calls, but do not match CDN/media hosts.
                        // Sentry trace headers trigger CORS preflights that signed CloudFront/S3 media URLs may reject.
                        /^https:\/\/(?!cdn[.-])[^/]+\.edverum\.(com|cloud)(?:\/|$)/,
                    ],
                    // Add educational workflow detection
                    beforeNavigate: context => {
                        // Add educational context to navigation spans
                        const routeName = context.to?.name || context.to?.path || '';

                        // Detect educational workflows
                        if (routeName.includes('submissions') || routeName.includes('submit')) {
                            context.tags = { ...context.tags, workflow: 'submission_processing' };
                        } else if (routeName.includes('gradebook') || routeName.includes('grading')) {
                            context.tags = { ...context.tags, workflow: 'grading' };
                        } else if (routeName.includes('quiz') || routeName.includes('assessment')) {
                            context.tags = { ...context.tags, workflow: 'quiz' };
                        } else if (routeName.includes('course')) {
                            context.tags = { ...context.tags, workflow: 'course_management' };
                        }

                        return context;
                    },
                }),

                // CONSULTATION ENHANCEMENT: Advanced session replay with educational privacy controls
                // Sample rates live on Sentry.init via getReplaySampleRates() — not here.
                new Sentry.Replay({
                    // CONSULTATION REQUIREMENT: Privacy settings for educational content
                    maskAllText: false, // Allow some text for debugging, but mask sensitive areas
                    maskAllInputs: true, // Always mask inputs (passwords, personal info)
                    blockAllMedia: false, // Allow media for video/audio debugging

                    // Network request/response capture for API debugging
                    networkDetailAllowUrls: [window.location.origin],
                    networkCaptureBodies: true,
                    networkRequestHeaders: ['X-CSRF-TOKEN', 'Authorization'],
                    networkResponseHeaders: ['X-Request-ID', 'X-RateLimit-Remaining'],

                    // CONSULTATION ADDITION: Mask sensitive educational data
                    mask: [
                        // Educational sensitive selectors
                        '.password-input',
                        '.ssn-input',
                        '.credit-card',
                        '[data-sensitive]',
                        '[data-student-info]',
                        '.student-grades',
                        '.personal-info',
                        // Quiz answer areas during active attempts
                        '.quiz-answer[data-active="true"]',
                        '.quiz-response-area',
                        // Grade input areas
                        '.grade-input',
                        '.rubric-score-input',
                    ],

                    // Block certain interactions from being recorded
                    block: ['.admin-panel', '.instructor-notes-private', '.confidential-comments'],

                    // Ignore certain DOM mutations to reduce noise
                    ignoreSelectors: ['.tooltip', '.notification-badge', '.loading-spinner', '.auto-save-indicator'],
                }),
            ],
            beforeSend(event, hint) {
                if (initialState('Debug')) {
                    return null;
                }

                // Filter out cancellation errors using centralized predicate
                const error = hint?.originalException;
                if (error && isCancellationError(error)) {
                    return null;
                }

                try {
                    // Enhanced context for all events
                    const browserInfo = safeGetBrowserInfo();
                    event.extra = {
                        ...event.extra,
                        ...browserInfo,
                    };

                    // Add user context if available from store
                    const userContext = safeGetUserContext();
                    if (userContext) {
                        event.user = userContext;
                    }

                    // Add current route context
                    const routeContext = safeGetRouteContext(router);
                    if (Object.keys(routeContext.tags).length > 0) {
                        event.tags = {
                            ...event.tags,
                            ...routeContext.tags,
                        };

                        event.extra.route_context = routeContext.context;
                    }

                    // Add educational context matching backend implementation
                    try {
                        const currentRoute = routeContext.context;
                        const routePath = currentRoute.path || '';

                        // Extract educational IDs from route parameters
                        if (currentRoute.params) {
                            const { params } = currentRoute;
                            if (params.courseId) event.tags.course_id = String(params.courseId);
                            if (params.activityId) event.tags.activity_id = String(params.activityId);
                            if (params.submissionId) event.tags.submission_id = String(params.submissionId);
                            if (params.quizId) event.tags.quiz_id = String(params.quizId);
                            if (params.userId) event.tags.student_id = String(params.userId);
                            if (params.semesterId) event.tags.semester_id = String(params.semesterId);
                        }

                        // Detect educational workflows (matching backend patterns)
                        if (routePath.includes('submissions') || routePath.includes('submit-activity')) {
                            event.tags.workflow = 'submission_processing';
                            event.tags.workflow_priority = 'high';
                        } else if (routePath.includes('gradebook') || routePath.includes('grading')) {
                            event.tags.workflow = 'grading';
                            event.tags.workflow_priority = 'high';
                        } else if (routePath.includes('quiz') || routePath.includes('assessment')) {
                            event.tags.workflow = 'quiz';
                            event.tags.workflow_priority = 'medium';
                        } else if (routePath.includes('course')) {
                            event.tags.workflow = 'course_management';
                            event.tags.workflow_priority = 'medium';
                        } else if (routePath.includes('upload') || routePath.includes('media')) {
                            event.tags.workflow = 'file_processing';
                            event.tags.workflow_priority = 'medium';
                        } else if (routePath.includes('dashboard') || routePath.includes('analytics')) {
                            event.tags.workflow = 'analytics';
                            event.tags.workflow_priority = 'low';
                        }

                        // Add interface tag
                        event.tags.interface = 'web_frontend';

                        // Add academic term context (matching backend logic)
                        const now = new Date();
                        const currentYear = now.getFullYear();
                        const academicYear = now.getMonth() >= 7 ? currentYear : currentYear - 1; // Aug-July

                        let term = 'summer';
                        if (now.getMonth() >= 7 && now.getMonth() <= 11) {
                            term = 'fall';
                        } else if (now.getMonth() >= 0 && now.getMonth() <= 4) {
                            term = 'spring';
                        }

                        event.tags.academic_year = String(academicYear);
                        event.tags.academic_term = term;

                        // Add platform identification
                        event.tags.platform = 'edverum';
                        event.tags.platform_component = 'frontend';
                    } catch (contextError) {
                        // Don't fail the entire error reporting if context gathering fails
                        console.warn('Failed to add educational context to Sentry event:', contextError);
                    }
                } catch (error) {
                    // If context gathering fails, don't let it break error reporting
                    console.warn('Failed to gather context for Sentry event:', error);
                }

                // Stale-chunk load failures (content-hashed filenames that vanish on
                // deploy) are owned by @/router/chunkLoadErrorHandler.js, which reports
                // each occurrence once via captureMessage tagged `outcome`. Drop the raw
                // error only when recovery confirms it has taken the failure; otherwise
                // report it as before, so a broken component never disappears silently.
                // Match by message, not type: Vite throws TypeError/Error, never
                // webpack's ChunkLoadError, and chained errors put the outermost last.
                const chunkException = (event.exception?.values ?? []).find(exception =>
                    isChunkLoadError(exception.value)
                );

                if (isChunkLoadError(hint?.originalException) || chunkException) {
                    return recoverChunkError(hint?.originalException ?? chunkException?.value) ? null : event;
                }

                // Filter out expected 401 errors on public routes (especially home route)
                // These are handled by axios interceptors and UnauthenticatedInterceptor
                const exceptionValue = event.exception?.values?.[0]?.value || '';
                const routePath = event.tags?.route_path || event.extra?.route_context?.path || '';
                const currentPath = router?.currentRoute?.value?.path || window.location.pathname || routePath;
                const isPublicRoute = isPublicRoutePath(currentPath);

                // Check if this is a 401 error on a public route
                // Use word boundary to avoid matching unintended strings (e.g., "14012")
                if (
                    (/\b401\b/.test(exceptionValue) ||
                        exceptionValue.includes('Request failed with status code 401')) &&
                    isPublicRoute
                ) {
                    // Expected authentication check - ignore
                    return null;
                }

                try {
                    // Enhanced fingerprinting for better error grouping
                    if (!event.fingerprint && event.exception?.values?.[0]) {
                        const exception = event.exception.values[0];
                        const fingerprint = [];

                        // Include error type
                        if (exception.type) {
                            fingerprint.push(exception.type);
                        }

                        // For HTTP errors, include the first line of the message (which should contain status/endpoint)
                        if (exception.value && exception.value.includes('HTTP ')) {
                            const httpErrorLine = exception.value.split('\n')[0];
                            fingerprint.push(httpErrorLine.replace(/\d+/g, '[id]').replace(/[a-f0-9-]{36}/g, '[uuid]'));
                        } else if (exception.value) {
                            // For other errors, use the first line of the error message
                            fingerprint.push(exception.value.split('\n')[0]);
                        }

                        // Include the error location (function/file)
                        if (exception.stacktrace?.frames?.length > 0) {
                            const topFrame = exception.stacktrace.frames[exception.stacktrace.frames.length - 1];
                            if (topFrame.function) {
                                fingerprint.push(topFrame.function);
                            } else if (topFrame.filename) {
                                fingerprint.push(topFrame.filename.split('/').pop());
                            }
                        }

                        if (fingerprint.length > 0) {
                            event.fingerprint = fingerprint;
                        }
                    }
                } catch (error) {
                    // If fingerprinting fails, don't let it break error reporting
                    console.warn('Failed to create fingerprint for Sentry event:', error);
                }

                return event;
            },
        });

        // Vue error handler
        app.config.errorHandler = (err, instance, info) => {
            // Stale-chunk failures are recovered by @/router/chunkLoadErrorHandler.js.
            // Suppress the generic error snackbar only once recovery owns the failure;
            // otherwise fall through so a broken component is still surfaced.
            if (isChunkLoadError(err) && recoverChunkError(err)) {
                return;
            }

            try {
                const errorPackage = new ErrorPackage(initialState('Debug'));
                errorPackage.capture(err, instance, info);

                // Generate a user-friendly message using the new method
                const userMessage = errorPackage.getUserFriendlyMessage();

                // Handle null returns (e.g., access denied errors)
                if (userMessage === null) {
                    errorPackage.disableSnackbar();
                    errorPackage.userMessage = null;
                } else {
                    errorPackage.userMessage = userMessage;

                    // In production, be more selective about which errors show snackbars
                    if (!initialState('Debug')) {
                        // Show snackbars for HTTP errors and network errors, but not for most Vue component errors
                        if (
                            err?.response ||
                            err?.message?.includes('Network Error') ||
                            err?.message?.includes('fetch') ||
                            err?.message?.includes('ChunkLoadError')
                        ) {
                            errorPackage.shouldShowSnackbar = true;
                        } else {
                            // Don't show snackbars for most Vue component errors in production
                            errorPackage.shouldShowSnackbar = false;
                        }
                    }
                }

                store.commit('PUSH_GLOBAL_ERROR', errorPackage);
            } catch (error) {
                // If our error handler fails, at least log to console
                console.error('Vue error handler failed:', error);
                console.error('Original error:', err);
            }
        };

        // Global unhandled promise rejection handler
        window.addEventListener('unhandledrejection', event => {
            try {
                const reason = event.reason;

                // Check if reason is null or undefined (Safari-specific issue)
                if (reason == null) {
                    return;
                }

                // Handed to @/router/chunkLoadErrorHandler.js; suppressed only once it
                // confirms ownership, otherwise reported below as any other rejection.
                if (isChunkLoadError(reason) && recoverChunkError(reason)) {
                    return;
                }

                // Ignore cancellation errors using centralized predicate
                if (isCancellationError(reason)) {
                    return;
                }

                // Normalize reason message - event.reason can be a string or an object
                const reasonMessage =
                    typeof reason === 'string' ? reason : typeof reason?.message === 'string' ? reason.message : '';

                // Check against ignore patterns
                if (
                    ignoreErrors.some(pattern => {
                        if (pattern instanceof RegExp) {
                            return pattern.test(reasonMessage);
                        }
                        if (typeof pattern === 'string') {
                            return reasonMessage === pattern;
                        }
                        return false;
                    })
                ) {
                    return;
                }

                if (isAccessDeniedError(reason)) {
                    event.preventDefault();
                    return;
                }

                // Filter out expected HTTP errors (401, 403, 404, etc.) on public routes
                // These are handled by axios interceptors and UnauthenticatedInterceptor
                const statusCode = reason?.response?.status;
                const currentPath = router?.currentRoute?.value?.path || window.location.pathname;
                const isPublicRoute = isPublicRoutePath(currentPath);

                // Expected HTTP error codes that should be filtered (matching axios.js:148)
                const expectedErrorCodes = [400, 401, 403, 404, 412, 422, 429];

                // If it's an expected error code on a public route (especially 401 on home), ignore it
                if (statusCode && expectedErrorCodes.includes(statusCode) && isPublicRoute) {
                    // Silently ignore - these are expected authentication checks
                    return;
                }

                Sentry.captureException(reason, {
                    extra: {
                        type: 'unhandledrejection',
                        promise: event.promise,
                    },
                });
            } catch (error) {
                console.error('Failed to handle unhandled promise rejection:', error);
            }
        });

        // Global error handler
        window.addEventListener('error', event => {
            try {
                // Filter null errors and line 0 (browser extensions)
                if (!event.error || (event.lineno === 0 && event.colno === 0)) {
                    return;
                }

                if (
                    ignoreErrors.some(pattern => {
                        if (pattern instanceof RegExp) {
                            return pattern.test(event.message);
                        }
                        if (typeof pattern === 'string') {
                            return event.message === pattern;
                        }
                        return false;
                    })
                ) {
                    return;
                }

                Sentry.captureException(event.error, {
                    extra: {
                        type: 'window.error',
                        filename: event.filename,
                        lineno: event.lineno,
                        colno: event.colno,
                    },
                });
            } catch (error) {
                console.error('Failed to handle window error:', error);
            }
        });

        // Add global methods to Vue instance
        app.config.globalProperties.$captureError = (error, context = {}) => {
            try {
                Sentry.captureException(error, {
                    extra: {
                        ...context,
                        timestamp: new Date().toISOString(),
                        component: context.component || 'unknown',
                        action: context.action || 'unknown',
                    },
                });
            } catch (sentryError) {
                console.error('Failed to capture error in Sentry:', sentryError);
                console.error('Original error:', error);
            }
        };

        app.config.globalProperties.$captureMessage = (message, level = 'info', context = {}) => {
            try {
                Sentry.captureMessage(message, {
                    level,
                    extra: {
                        ...context,
                        timestamp: new Date().toISOString(),
                    },
                });
            } catch (error) {
                console.error('Failed to capture message in Sentry:', error);
            }
        };
    },
};
